import { ArrowRight, Check, ClipboardCheck, Loader2, X } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { useCrmProposal } from '../hooks/useCrmProposal';
import { SoteriaCard, soteriaCardCtaClass } from './SoteriaCard';
import type {
  CrmProposalCard as CrmProposalCardData,
  CrmProposalChange,
} from '../hooks/useSoteriaChat';
import { useAdvisorCopy } from '@/shared/hooks/useAdvisorCopy';
import { cn } from '@/lib/utils';

/**
 * One before -> after row. A change with no `before` is an addition and shows
 * just the new value. Values wrap rather than truncate: a timeline note is the
 * one thing the advisor most needs to read in full before saving it.
 */
export function ChangeRows({ changes }: { changes: Array<CrmProposalChange> }) {
  const copy = useAdvisorCopy();

  return (
    <dl className="mt-3 space-y-2 rounded-lg bg-muted/50 p-3 text-sm">
      {changes.map((c) => (
        <div key={c.label} className="flex flex-col gap-0.5">
          <dt className="text-xs text-muted-foreground">{c.label}</dt>
          <dd className="whitespace-pre-wrap break-words font-medium text-foreground">
            {c.before ? (
              <>
                <span className="font-normal text-muted-foreground line-through">
                  {c.before}
                </span>
                <ArrowRight
                  aria-label={copy('changes to')}
                  className="mx-1.5 inline h-3 w-3 text-muted-foreground"
                />
              </>
            ) : null}
            {c.after ?? (
              <span className="text-muted-foreground">{copy('Empty')}</span>
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * CrmProposalCard
 * ---------------
 * A CRM write the Client Assistant proposed, waiting for the advisor.
 *
 * The assistant never saves anything itself. This card shows exactly what would
 * be written, to which client, and Confirm is the only thing that writes it:
 * POST /soteria/proposals/{id}/confirm, made with the advisor's own session. The
 * server fixed the content when it was proposed, re-checks owner, chat, org and
 * expiry when this is clicked, and saves once however many times it is pressed;
 * the card only relays what it answers.
 */
export function CrmProposalCard({
  proposal,
  sessionId,
}: {
  proposal: CrmProposalCardData;
  sessionId: string | null | undefined;
}) {
  const copy = useAdvisorCopy();

  const { phase, message, summary, clientId, busy, canAct, confirm, cancel } =
    useCrmProposal(proposal, sessionId);

  if (phase === 'confirmed') {
    return (
      <SoteriaCard
        variant="success"
        icon={<Check className="h-4 w-4" />}
        eyebrow={copy('Saved')}
        title={summary ?? `${copy('Saved')}: ${proposal.client_name ?? proposal.title}`}
        cta={
          clientId ? (
            <Link
              to="/clients/$clientId"
              params={{ clientId }}
              className={soteriaCardCtaClass('success')}
            >
              {copy('Open')}
              <ArrowRight className="h-3 w-3" />
            </Link>
          ) : undefined
        }
      />
    );
  }

  if (phase === 'cancelled') {
    return (
      <SoteriaCard
        icon={<X className="h-4 w-4" />}
        eyebrow={copy('Cancelled')}
        title={proposal.title}
        subtitle={copy('Nothing was saved.')}
      />
    );
  }

  if (phase === 'expired' || phase === 'refused' || phase === 'uncertain') {
    return (
      <SoteriaCard
        variant="warning"
        icon={<ClipboardCheck className="h-4 w-4" />}
        eyebrow={
          phase === 'expired'
            ? copy('Expired')
            : phase === 'uncertain'
              ? copy('Check result')
              : copy('Not saved')
        }
        title={proposal.title}
        subtitle={message}
      />
    );
  }

  return (
    <SoteriaCard
      variant={proposal.warning ? 'warning' : 'default'}
      icon={<ClipboardCheck className="h-4 w-4" />}
      eyebrow={copy('Review and confirm: nothing is saved yet')}
      title={proposal.title}
      subtitle={
        proposal.client_reference
          ? `${proposal.client_name ?? ''} · ${proposal.client_reference}`
          : undefined
      }
    >
      <ChangeRows changes={proposal.changes} />
      {proposal.note && (
        <p
          className={cn(
            'mt-2 text-xs',
            proposal.warning
              ? 'font-medium text-foreground'
              : 'text-muted-foreground',
          )}
        >
          {proposal.note}
        </p>
      )}
      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => void cancel()}
          disabled={busy || !canAct}
          className={cn(soteriaCardCtaClass(), 'disabled:opacity-60')}
        >
          {copy('Cancel')}
        </button>
        <button
          type="button"
          onClick={() => void confirm()}
          disabled={busy || !canAct}
          className={cn(soteriaCardCtaClass(), 'gap-1.5 disabled:opacity-60')}
        >
          {busy && <Loader2 className="h-3 w-3 animate-spin" />}
          {copy('Confirm')}
        </button>
      </div>
    </SoteriaCard>
  );
}
